import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";

// Layouts
import TieredProduct from "@/Layouts/components/TieredProduct/TieredProduct";

// Custom components
import MakeModelSelector from "../MakeModelSelector";

export default function ModelTiered(props) {
	const {
		index,
		modelKey = "",
		delta = 0.5,
		frontProducts = [],
		backProducts = [],
		makes = [],
		model = {
			make: "",
			model: "",
			modelYears: { rangeType: "RELATIVE", left: null, right: null },
			tiers: [],
		},
		modelsAddons = {
			regularExpression: false,
			miniDeal: false,
			applyRetroactively: false,
		},
		error = false,
		loading = false,
		loadingProducts = false,
		loadingMakes = false,
		readOnly = false,
		useRegexModels = false,
		errors,
		onActionDelete = () => {},
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedModel) => {
		const isValid = Boolean(isValidTiers);
		onActionValidation?.(isValid);
		if (wasUpdated.current) {
			onActionUpdate?.(updatedModel);
		}
	};

	const [innerModel, setModel] = useCallbackState(
		{
			make: "",
			model: "",
			modelYears: { rangeType: "RELATIVE", left: null, right: null },
			tiers: [],
		},
		onModelUpdate
	);

	const [isValidTiers, setIsValidTiers] = useState(true);

	const save = (area, data) => {
		let updatedModel = { ...innerModel };
		if (area === "tiers" || area === "miniDeal") {
			updatedModel = { ...updatedModel, [area]: data };
		} else {
			updatedModel = { ...updatedModel, ...data };
		}
		wasUpdated.current = true;
		setModel(updatedModel);
	};

	useEffect(() => {
		setModel(model);

		return () => {
			setModel({
				make: "",
				model: "",
				modelYears: { rangeType: "RELATIVE", left: null, right: null },
				tiers: [],
			});
			wasUpdated.current = false;
			setIsValidTiers(true);
		};
	}, [model, setModel]);

	const { tiers: innerTiers = [], miniDeal = undefined, ...modelProperties } = innerModel || {};

	return (
		<MakeModelSelector
			{...modelProperties}
			index={index}
			modelKey={modelKey}
			allowSpecificModels={Boolean(modelKey !== "new")}
			makes={makes}
			modelsAddons={modelsAddons}
			loading={loading || loadingMakes}
			readOnly={readOnly}
			errors={errors}
			useRegexModels={useRegexModels}
			onActionDelete={onActionDelete}
			onActionUpdate={(resultModel) => save("model", resultModel)}
		>
			<TieredProduct
				modelKey={modelKey}
				modelsAddons={modelsAddons}
				tiers={innerTiers}
				delta={delta}
				frontProducts={frontProducts}
				backProducts={backProducts}
				miniDeal={miniDeal}
				loadingProducts={loading || loadingProducts}
				additionalErrors={error ? ["Invalid model and years definition"] : []}
				readOnly={readOnly}
				onActionUpdate={(data, area) => save(area, data)}
				onActionValidation={(value) => {
					setIsValidTiers(value);
				}}
			/>
		</MakeModelSelector>
	);
}
ModelTiered.propTypes = {
	modelKey: PropTypes.string,
	delta: PropTypes.number,
	frontProducts: PropTypes.array,
	backProducts: PropTypes.array,
	makes: PropTypes.array,
	model: PropTypes.shape({
		make: PropTypes.string,
		model: PropTypes.string,
		modelYears: PropTypes.shape({
			rangeType: PropTypes.string,
			left: PropTypes.number,
			right: PropTypes.number,
		}),
		tiers: PropTypes.array,
	}),
	modelsAddons: PropTypes.shape({
		regularExpression: PropTypes.bool,
		miniDeal: PropTypes.bool,
		applyRetroactively: PropTypes.bool,
	}),
	errors: PropTypes.object,
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	useRegexModels: PropTypes.bool,
	onActionDelete: PropTypes.func,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
